// src/store/index.ts
//
// Stato della sessione (Vuex). Tema e sidebar stanno invece in store Pinia
// (theme.ts, sidebar.ts).
//
// L'utente e i suoi permessi vivono anche in localStorage (`user`): è da lì
// che li legge api/permessi.ts, così router e menu non dipendono dallo store.

import { createStore } from 'vuex'
import { apiAuth } from '@/api/apiAuth'
import type { UtenteSessione } from '@/api/permessi'

interface RootState {
  isAuthenticated: boolean
  user: UtenteSessione | null
  authToken: string | null
  /** Token già verificato con /checktoken in questa sessione di navigazione. */
  tokenChecked: boolean
}

export enum ActionTypes {
  LOGIN = 'LOGIN',
  LOGOUT = 'LOGOUT',
  CHECK_AUTH_ON_LOAD = 'CHECK_AUTH_ON_LOAD',
}

export enum MutationTypes {
  SET_SESSION = 'SET_SESSION',
  CLEAR_SESSION = 'CLEAR_SESSION',
  SET_USER = 'SET_USER',
  SET_TOKEN_CHECKED = 'SET_TOKEN_CHECKED',
}

function utenteSalvato(): UtenteSessione | null {
  try {
    return JSON.parse(localStorage.getItem('user') || 'null')
  } catch {
    return null
  }
}

const store = createStore<RootState>({
  state: {
    isAuthenticated: false,
    user: null,
    authToken: null,
    tokenChecked: false,
  },
  mutations: {
    [MutationTypes.SET_SESSION](state, payload: { token: string; user: UtenteSessione | null }) {
      state.isAuthenticated = true
      state.authToken = payload.token
      state.user = payload.user
    },
    [MutationTypes.CLEAR_SESSION](state) {
      state.isAuthenticated = false
      state.authToken = null
      state.user = null
      state.tokenChecked = false
    },
    [MutationTypes.SET_USER](state, user: UtenteSessione | null) {
      state.user = user
    },
    [MutationTypes.SET_TOKEN_CHECKED](state, value: boolean) {
      state.tokenChecked = value
    },
  },
  actions: {
    /** Chiamata da AuthCallback dopo il redirect SSO, con il token già validato. */
    [ActionTypes.LOGIN]({ commit }, data: { access_token: string; user: UtenteSessione | null }) {
      localStorage.setItem('authToken', data.access_token)
      localStorage.setItem('user', JSON.stringify(data.user ?? null))
      commit(MutationTypes.SET_SESSION, { token: data.access_token, user: data.user ?? null })
      commit(MutationTypes.SET_TOKEN_CHECKED, true)
    },

    /** Revoca il token sul server, poi pulisce la sessione locale. */
    async [ActionTypes.LOGOUT]({ commit, state }) {
      if (state.authToken || localStorage.getItem('authToken')) {
        await apiAuth.logout()
      }
      localStorage.removeItem('authToken')
      localStorage.removeItem('user')
      commit(MutationTypes.CLEAR_SESSION)
    },

    /**
     * All'avvio ripristina la sessione da localStorage, senza chiamate di rete:
     * la validità del token la verifica il guard del router con /checktoken.
     */
    [ActionTypes.CHECK_AUTH_ON_LOAD]({ commit }) {
      const token = localStorage.getItem('authToken')
      if (token) {
        commit(MutationTypes.SET_SESSION, { token, user: utenteSalvato() })
      } else {
        commit(MutationTypes.CLEAR_SESSION)
      }
    },
  },
})

export default store
